// pages/home/Dashboard.jsx
// 📍 Updated with location switching

import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Card,
  Button,
  message,
  Modal,
  Row,
  Col,
  Spin,
  Typography,
  Space,
  Tag,
} from "antd";
import {
  ClockCircleOutlined,
  CheckCircleOutlined,
  DesktopOutlined,
  MobileOutlined,
  PlusOutlined,
  CrownOutlined,
  AppstoreOutlined,
  PrinterOutlined,
  DashboardOutlined,
  RocketOutlined,
  CheckCircleFilled,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import LiquidFillGauge from "react-liquid-gauge";
import { color } from "d3-color";
import { interpolateRgb } from "d3-interpolate";
import "./style.css";
import { BACKEND_URL } from "../../constant";
import {
  useFetchSelfRestaurantDataQuery,
  useUpdateRestaurantSettingMutation,
} from "../restaurant/restaruant.service";
import { useFetchAdvertQuery } from "../advert/advert.service";
import { useFetchChecklistSettingsQuery } from "../settings/setting.service";
import LocationSelector from "../../components/LocationSelector";

const { Title, Paragraph, Text   } = Typography;

const Dashboard = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const userData = useSelector((apps) => apps.app.user);
  const isAdmin = userData?.role === "admin" || userData?.role === "super_admin";

  // ✅ Location state
  const [locationId, setLocationId] = useState(null);
  const [showChoiceModal, setShowChoiceModal] = useState(false);
  const [showReopenPrompt, setShowReopenPrompt] = useState(false);

  const { data: checklistSettingsResponse } = useFetchChecklistSettingsQuery(
    locationId
  );
  const checklistSettings = checklistSettingsResponse?.data || {};
  const isMonthlyVisible = checklistSettings?.monthly === true;

  const [updateRestaurantSetting] = useUpdateRestaurantSettingMutation();

  // ✅ Fetch restaurant data with locationId
  const {
    data = {
      data: {
        restaurant: {
          openChecklistCompleted: false,
          closeChecklistCompleted: false,
          hotholdChecklistCompleted: false,
          weeklyChecklistCompleted: false,
          monthlyChecklistCompleted: false,
          complianceLevel: [],
          isHolidayMode: false,
          holidayEnd: null,
        },
      },
    },
    refetch: refetchRestaurant,
  } = useFetchSelfRestaurantDataQuery(locationId, {
    refetchOnMountOrArgChange: true,
  });

  const {
    data: restaurantData,
    isLoading: isRestaurantLoading,
  } = useFetchSelfRestaurantDataQuery(locationId, {
    refetchOnMountOrArgChange: true,
  });

  const {
    data: {
      restaurant: {
        openChecklistCompleted,
        closeChecklistCompleted,
        hotholdChecklistCompleted,
        weeklyChecklistCompleted,
        monthlyChecklistCompleted,
        complianceLevel,
      },
    },
  } = restaurantData || data;

  const { data: advertData = { data: { adverts: [] } } } = useFetchAdvertQuery();
  const { data: { adverts } } = advertData;

  // ✅ Handle location change
  const handleLocationChange = (newLocationId) => {
    setLocationId(newLocationId);
    // Data will auto-refetch due to locationId change
  };

  // ✅ Check first login wizard
  useEffect(() => {
    const wizardJustCompleted = localStorage.getItem("wizardJustCompleted") === "true";
    if (wizardJustCompleted) {
      setShowChoiceModal(true);
      localStorage.removeItem("wizardJustCompleted");
    }
  }, []);

  // ✅ Check holiday mode
  useEffect(() => {
    const restaurant = restaurantData?.data?.restaurant;
    if (restaurant?.isHolidayMode && restaurant?.holidayEnd) {
      const holidayEnd = new Date(restaurant.holidayEnd);
      const now = new Date();
      setShowReopenPrompt(now <= holidayEnd);
    } else {
      setShowReopenPrompt(false);
    }
  }, [restaurantData]);

  const handleReopenKitchen = async () => {
    await updateRestaurantSetting({ isHolidayMode: false }).unwrap();
    message.success("Kitchen reopened!");
    setShowReopenPrompt(false);
    window.location.reload();
  };

  const handleRemindLater = () => setShowReopenPrompt(false);

  const redirectToWebAdmin = () => {
    // Already on the admin SPA — just close the choice dialog and show Home
    setShowChoiceModal(false);
    navigate("/home", { replace: true });
  };

  const openMobileApp = () => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    const isAndroid = /android/i.test(userAgent);
    const isIOS = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;

    const androidAppUrl =
      "https://play.google.com/store/apps/details?id=com.sfbbpro.app";
    const iosAppUrl = "https://apps.apple.com/us/app/sfbb-pro/id6749208075";
    const appScheme = "sfbbpro://dashboard";

    if (isAndroid) {
      const intentUrl = `intent://dashboard#Intent;scheme=sfbbpro;package=com.sfbbpro.app;S.browser_fallback_url=${encodeURIComponent(androidAppUrl)};end`;
      window.location.href = intentUrl;
      setTimeout(() => {
        window.location.href = androidAppUrl;
      }, 2500);
    } else if (isIOS) {
      window.location.href = appScheme;
      setTimeout(() => {
        window.location.href = iosAppUrl;
      }, 2500);
    } else {
      message.info({
        content: "Please scan QR code from your mobile device to download the app",
        duration: 3,
      });
    }
  };

  const startColor = "#6db33f";
  const endColor = "#6db33f";
  const radius = window.innerWidth < 768 ? 150 : 200;
  const interpolate = interpolateRgb(startColor, endColor);
  const fillColor = interpolate(0.5);
  const gradientStops = [
    {
      key: "0%",
      stopColor: color(fillColor).darker(0.5).toString(),
      stopOpacity: 1,
      offset: "0%",
    },
    {
      key: "50%",
      stopColor: fillColor,
      stopOpacity: 0.75,
      offset: "50%",
    },
    {
      key: "100%",
      stopColor: color(fillColor).brighter(0.5).toString(),
      stopOpacity: 0.5,
      offset: "100%",
    },
  ];

  const isLoading = isRestaurantLoading;

  return (
    <>
      {/* Reopen Kitchen Modal */}
      <Modal
        title="🔄 Kitchen Reopening"
        open={showReopenPrompt}
        closable={false}
        footer={[
          <Button key="remind" onClick={handleRemindLater}>
            Remind Later
          </Button>,
          <Button key="reopen" type="primary" onClick={handleReopenKitchen}>
            Reopen Kitchen
          </Button>,
          <Button key="settings" onClick={() => navigate("/settings")}>
            Settings
          </Button>,
        ]}
      >
        <div style={{ textAlign: "center", padding: "20px" }}>
          <ClockCircleOutlined style={{ fontSize: 48, color: "#faad14" }} />
          <h3>Holiday Period Has Ended</h3>
          <p>Do you want to reopen your kitchen?</p>
        </div>
      </Modal>

      {/* Choice Modal */}
      <Modal
        title="Setup Complete! 🎉"
        open={showChoiceModal}
        footer={null}
        width={window.innerWidth < 768 ? "90%" : 700}
        closable={false}
        maskClosable={false}
        keyboard={false}
        className="choice-modal"
        style={{ top: 20 }}
        bodyStyle={{ padding: window.innerWidth < 768 ? "16px" : "24px" }}
      >
        <div style={{ textAlign: "center" }}>
          <CheckCircleOutlined
            style={{
              fontSize: window.innerWidth < 768 ? 48 : 64,
              color: "#52c41a",
              marginBottom: 16,
            }}
          />
          <Title level={window.innerWidth < 768 ? 4 : 3} style={{ marginTop: 0 }}>
            Your equipment has been configured!
          </Title>
          <Paragraph
            style={{
              fontSize: window.innerWidth < 768 ? 14 : 16,
              marginBottom: 24,
            }}
          >
            Choose where you want to go:
          </Paragraph>

          <Row gutter={[16, 16]} justify="center">
            <Col xs={24} sm={24} md={12}>
              <Card
                hoverable
                onClick={openMobileApp}
                style={{
                  cursor: "pointer",
                  textAlign: "center",
                  height: "100%",
                  borderRadius: 12,
                  transition: "all 0.3s ease",
                }}
                bodyStyle={{
                  padding: window.innerWidth < 768 ? "16px" : "24px",
                }}
              >
                <MobileOutlined
                  style={{
                    fontSize: window.innerWidth < 768 ? 40 : 48,
                    color: "#47e147",
                  }}
                />
                <Title level={window.innerWidth < 768 ? 5 : 4} style={{ marginTop: 12, marginBottom: 8 }}>
                  Mobile App
                </Title>
                <Paragraph
                  style={{
                    color: "#666",
                    fontSize: window.innerWidth < 768 ? 12 : 14,
                    marginBottom: 16,
                  }}
                >
                  Daily operations, fill checklists,
                  <br />
                  temperature tracking, generate reports
                </Paragraph>
                <Button
                  type="primary"
                  size={window.innerWidth < 768 ? "middle" : "large"}
                  style={{ marginTop: 8, background: "#47e147", borderColor: "#47e147" }}
                >
                  Open Mobile App →
                </Button>
              </Card>
            </Col>

            <Col xs={24} sm={24} md={12}>
              <Card
                hoverable
                onClick={redirectToWebAdmin}
                style={{
                  cursor: "pointer",
                  textAlign: "center",
                  height: "100%",
                  borderRadius: 12,
                  transition: "all 0.3s ease",
                }}
                bodyStyle={{
                  padding: window.innerWidth < 768 ? "16px" : "24px",
                }}
              >
                <DesktopOutlined
                  style={{
                    fontSize: window.innerWidth < 768 ? 40 : 48,
                    color: "#1890ff",
                  }}
                />
                <Title level={window.innerWidth < 768 ? 5 : 4} style={{ marginTop: 12, marginBottom: 8 }}>
                  Web Admin Panel
                </Title>
                <Paragraph
                  style={{
                    color: "#666",
                    fontSize: window.innerWidth < 768 ? 12 : 14,
                    marginBottom: 16,
                  }}
                >
                  Manage your restaurant, create/edit checklists,
                  <br />
                  add/remove fridges & freezers
                </Paragraph>
                <Button type="primary" size={window.innerWidth < 768 ? "middle" : "large"} style={{ marginTop: 8 }}>
                  Stay on Web admin panel
                </Button>
              </Card>
            </Col>
          </Row>

          <Paragraph
            style={{
              marginTop: 24,
              color: "#999",
              fontSize: window.innerWidth < 768 ? 10 : 12,
            }}
          >
            You can always switch between Web Admin and Mobile App anytime.
          </Paragraph>
        </div>
      </Modal>

      {/* ✅ Main Dashboard Card */}
      <Card
        title={
          <div style={{ display: "flex", alignItems: "center", gap: "12px", flexWrap: "wrap" }}>
            <span style={{ fontSize: "20px", fontWeight: "bold" }}>{t("HOME")}</span>
            {/* ✅ Location Selector - Shows only for admin with multiple locations */}
            <LocationSelector onLocationChange={handleLocationChange} />
          </div>
        }
        style={{ fontSize: "20px" }}
        className="home-page"
        bordered={false}

      >
        
        <Spin spinning={isLoading}>

          {/* Opening Checks */}
          <div className="home-page-check-list">
            <div className="home-page-check-list-label-default">
              {t("OPENING CHECKS")}
            </div>
            <div
              className={`home-page-check-list-status ${
                openChecklistCompleted
                  ? "home-page-check-list-status-complete"
                  : "home-page-check-list-status-pending"
              }`}
              onClick={() => {
                if (!openChecklistCompleted) navigate("/openchecks/create");
              }}
            >
              <div>
                {openChecklistCompleted ? t("COMPLETED") : t("CLICK TO FILL")}
              </div>
            </div>
          </div>

          {/* Probe Checks */}
          <div className="home-page-check-list">
            <div className="home-page-check-list-label-default">
              {t("PROBE CHECKS")}
            </div>
            <div
              className={`home-page-check-list-status ${
                hotholdChecklistCompleted
                  ? "home-page-check-list-status-complete"
                  : "home-page-check-list-status-pending"
              }`}
              onClick={() => {
                if (!hotholdChecklistCompleted) navigate("/provechecks/create");
              }}
            >
              <div>
                {hotholdChecklistCompleted ? t("COMPLETED") : t("CLICK TO FILL")}
              </div>
            </div>
          </div>

          {/* Closing Checks */}
          <div className="home-page-check-list">
            <div className="home-page-check-list-label-default">
              {t("CLOSING CHECKS")}
            </div>
            <div
              className={`home-page-check-list-status ${
                closeChecklistCompleted
                  ? "home-page-check-list-status-complete"
                  : "home-page-check-list-status-pending"
              }`}
              onClick={() => {
                if (!closeChecklistCompleted) navigate("/closechecks/create");
              }}
            >
              <div>
                {closeChecklistCompleted ? t("COMPLETED") : t("CLICK TO FILL")}
              </div>
            </div>
          </div>

          {/* Weekly Checks */}
          <div className="home-page-check-list">
            <div className="home-page-check-list-label-default">
              {t("WEEKLY CHECKS")}
            </div>
            <div
              className={`home-page-check-list-status ${
                weeklyChecklistCompleted
                  ? "home-page-check-list-status-complete"
                  : "home-page-check-list-status-pending"
              }`}
              onClick={() => {
                if (!weeklyChecklistCompleted) navigate("/weeklychecks/create");
              }}
            >
              <div>
                {weeklyChecklistCompleted ? t("COMPLETED") : t("CLICK TO FILL")}
              </div>
            </div>
          </div>

          {/* Monthly Checks */}
          {isMonthlyVisible && (
            <div className="home-page-check-list">
              <div className="home-page-check-list-label-default">
                {t("MONTHLY CHECKS")}
              </div>
              <div
                className={`home-page-check-list-status ${
                  monthlyChecklistCompleted
                    ? "home-page-check-list-status-complete"
                    : "home-page-check-list-status-pending"
                }`}
                onClick={() => {
                  if (!monthlyChecklistCompleted) navigate("/monthlychecks/create");
                }}
              >
                <div>
                  {monthlyChecklistCompleted ? t("COMPLETED") : t("CLICK TO FILL")}
                </div>
              </div>
            </div>
          )}

          {/* Deliveries */}
          <div className="home-page-check-list">
            <div className="home-page-check-list-label-default">
              {t("DELIVERIES")} - {t("ADD")}
            </div>
            <div
              className="home-page-check-list-status home-page-add-default"
              onClick={() => navigate("/delivery")}
            >
              {t("ADD DELIVERIES")}
            </div>
          </div>

          {/* Incidents */}
          <div className="home-page-check-list">
            <div className="home-page-check-list-label-default">
              {t("INCIDENTS")} - {t("ADD")}
            </div>
            <div
              className="home-page-check-list-status home-page-add-default"
              onClick={() => navigate("/incidents/create")}
            >
              {t("CREATE INCIDENT")}
            </div>
          </div>

          {/* Progress Gauges */}
          <div className="home-page-check-list">
            <div className="group">
              <h4>{t("DAILY TASKS COMPLETED")}</h4>
              <div className="home-page-check-list-progress">
                <LiquidFillGauge
                  style={{ margin: "0 auto" }}
                  width={radius}
                  height={radius}
                  value={
                    (openChecklistCompleted ? 33 : 0) +
                    (hotholdChecklistCompleted ? 33 : 0) +
                    (closeChecklistCompleted ? 34 : 0)
                  }
                  percent="%"
                  textSize={1}
                  textOffsetX={0}
                  textOffsetY={0}
                  textRenderer={(props) => {
                    const value = Math.round(props.value);
                    const radiusVal = Math.min(props.height / 2, props.width / 2);
                    const textPixels = (props.textSize * radiusVal) / 2;
                    return (
                      <tspan>
                        <tspan className="value" style={{ fontSize: textPixels }}>
                          {value}
                        </tspan>
                        <tspan style={{ fontSize: textPixels * 0.6 }}>
                          {props.percent}
                        </tspan>
                      </tspan>
                    );
                  }}
                  riseAnimation
                  waveAnimation
                  waveFrequency={2}
                  waveAmplitude={1}
                  gradient
                  gradientStops={gradientStops}
                  circleStyle={{ fill: fillColor }}
                  waveStyle={{ fill: fillColor }}
                  textStyle={{ fill: color("#444").toString(), fontFamily: "Arial" }}
                  waveTextStyle={{ fill: color("#fff").toString(), fontFamily: "Arial" }}
                />
              </div>
            </div>

            <div className="group">
              <h4>{t("COMPLIANCE LEVEL")}</h4>
              <div className="home-page-check-list-progress">
                <LiquidFillGauge
                  style={{ margin: "0 auto" }}
                  width={radius}
                  height={radius}
                  value={Math.floor((complianceLevel?.length || 0) / 6 * 100)}
                  percent="%"
                  textSize={1}
                  textOffsetX={0}
                  textOffsetY={0}
                  textRenderer={(props) => {
                    const value = Math.round(props.value);
                    const radiusVal = Math.min(props.height / 2, props.width / 2);
                    const textPixels = (props.textSize * radiusVal) / 2;
                    return (
                      <tspan>
                        <tspan className="value" style={{ fontSize: textPixels }}>
                          {value}
                        </tspan>
                        <tspan style={{ fontSize: textPixels * 0.6 }}>
                          {props.percent}
                        </tspan>
                      </tspan>
                    );
                  }}
                  riseAnimation
                  waveAnimation
                  waveFrequency={2}
                  waveAmplitude={1}
                  gradient
                  gradientStops={gradientStops}
                  circleStyle={{ fill: fillColor }}
                  waveStyle={{ fill: fillColor }}
                  textStyle={{ fill: color("#444").toString(), fontFamily: "Arial" }}
                  waveTextStyle={{ fill: color("#fff").toString(), fontFamily: "Arial" }}
                />
              </div>
            </div>
          </div>

          {/* Advertisement */}
          {adverts?.length > 0 && (
            <div className="home-page-check-list">
              <img
                src={`${BACKEND_URL}${adverts[0].imageUrl}`}
                onClick={() => window.open(adverts[0].websiteUrl, "_blank")}
                alt="Advertisement"
                style={{
                  margin: "auto",
                  width: "80%",
                  height: "auto",
                  objectFit: "cover",
                  border: "1px solid #d9d9d9",
                  borderRadius: "6px",
                  cursor: "pointer",
                }}
              />
            </div>
          )}
        </Spin>
      </Card>
    </>
  );
};

export default Dashboard;